// @flow

import type {Config} from 'docs/markdoc';
import type {UrlGenerator} from '../../../../server/services';
import type {ValidationMetadata} from '../../../../manifest/validators/getValidationConfig';

export const IMAGE_PREFIX = 'images/';
// The Markdoc images are stored in docs/content, which gets outputted
// to the webpack manifest as content/images/**
export const imageSrcToPublicUrl = (
  src: string,
  urlGenerator: UrlGenerator,
  locale?: ?string,
): string => {
  // We explictly check if it is a string because the src comes from a Markdoc
  // attribute, so we perform this check in case the attribute was incorrectly cast
  // to a string and passed to this function
  const shouldTransformSrc =
    typeof src === 'string' && src[0] !== '/' && !src.startsWith('http://');

  if (!shouldTransformSrc) {
    return src;
  }

  if (locale && locale !== 'en-US') {
    const localizedSrc = `${src.slice(
      0,
      IMAGE_PREFIX.length,
    )}intl/${locale}/${src.slice(IMAGE_PREFIX.length)}`;
    // This is the image we _think_ we might have based on locale
    const generatedLocalizedSrc = urlGenerator.generateFromPath(
      `content/${localizedSrc}`,
    );
    if (generatedLocalizedSrc) {
      return generatedLocalizedSrc;
    }
  }

  // If we cannot find localized image, proceed with the unlocalized source, which by default is an English image
  const generatedSrc = urlGenerator.generateFromPath(`content/${src}`);
  if (!generatedSrc) {
    // TODO(matv): Consider using Sentry here - right now this runs server side so we do
    // not have a Sentry client.
    // eslint-disable-next-line no-console
    console.error('Could not find', src, 'in webpack build');
    return src;
  }

  return generatedSrc;
};

export function validateImageSrc(src: any, config: any) {
  const validationMetadata: ValidationMetadata = config.validationMetadata;

  if (typeof src !== 'string') {
    return [
      {
        id: 'dynamic-image-src',
        level: 'warning',
        message:
          'Images should be explicit strings instead of dynamically generated. Reach out to #markdoc about your use case.',
      },
    ];
  }

  if (src.startsWith('http')) {
    return [];
  }

  if (!src.startsWith('images')) {
    return [
      {
        id: 'invalid-image-src',
        level: 'critical',
        message: `Images referenced in Markdoc should be placed in docs/content/images and the source should start with images/ - see http://go/markdoc-tag/image for more.`,
      },
    ];
  }

  if (!validationMetadata?.imageSources) {
    return [];
  }

  if (!validationMetadata.imageSources.includes(src)) {
    return [
      {
        id: 'image-src-does-not-exist',
        level: 'critical',
        message: `${src} not found.`,
      },
    ];
  }
  const locales = [
    'de-DE',
    'en-GB',
    'es-ES',
    'es-419',
    'fr-FR',
    'fr-CA',
    'id-ID',
    'it-IT',
    'ja-JP',
    'nl-NL',
    'pt-BR',
    'th-TH',
    'zh-Hans',
  ];
  const possibleIntlPaths = locales.map((locale) => [
    `${src.slice(0, IMAGE_PREFIX.length)}intl/${locale}/${src.slice(
      IMAGE_PREFIX.length,
    )}`,
    locale,
  ]);

  const missingLocales = [];
  for (const [path: string, locale: string] of possibleIntlPaths) {
    if (!validationMetadata.imageSources.includes(path)) {
      missingLocales.push(locale);
    }
  }

  if (missingLocales.length > 0) {
    return [
      {
        id: 'image-is-not-localized',
        level: 'warning',
        message: `Image is not localized for these locales: ${missingLocales.join(
          ', ',
        )}. Localized images should be placed at images/intl/locale`,
      },
    ];
  }

  return [];
}

export class ImageSrc {
  render(value: any, config: Config) {
    const locale: ?string = (config.variables.intl: any)?.locale;
    return imageSrcToPublicUrl(value, config.services.urlGenerator, locale);
  }

  validate(value: any, config: Config) {
    return validateImageSrc(value, config);
  }
}
